/**
 * Tab bar group enter / exit / collapse motion.
 *
 * Rule: animate inside the fixed 40px tab bar. Do not resize the shell column.
 * Gallery SoT: /?test=maater-ui#tab-groups
 *
 * Active-tab chrome (glow, punch-through, divider fade) uses the same
 * 200ms / TAB_GROUP_EASE in `_sidebar-nav.scss` (.tab-group__tab).
 */

export const TAB_GROUP_MOTION_MS = 200

/** Shared easing with shell panel motion. */
export const TAB_GROUP_EASE = 'cubic-bezier(0.4, 0, 0.2, 1)'

export type TabGroupMotionPhase = 'enter' | 'exit' | 'collapse' | 'expand' | 'idle'

function prefersReducedMotion(): boolean {
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
    return false
  }
  return window.matchMedia('(prefers-reduced-motion: reduce)').matches
}

/**
 * CSS class for a workspace tab group entering the bar (restore after Only, or expand).
 * Empty when reduced-motion (instant).
 */
export function tabGroupEnterClass(): string {
  if (prefersReducedMotion()) return ''
  return 'tab-group--enter'
}

/**
 * CSS class for a workspace tab group leaving the bar (Only / hide).
 * Empty when reduced-motion (instant unmount).
 */
export function tabGroupExitClass(): string {
  if (prefersReducedMotion()) return ''
  return 'tab-group--exit'
}

/**
 * CSS class for the tab strip inside a group when collapsing into the initials badge.
 */
export function tabGroupCollapseStripClass(collapsed: boolean): string {
  if (prefersReducedMotion()) {
    return collapsed ? 'tab-group__strip--collapsed-instant' : 'tab-group__strip--expanded-instant'
  }
  return collapsed ? 'tab-group__strip--collapsed' : 'tab-group__strip--expanded'
}

export function tabGroupMotionDurationMs(): number {
  return prefersReducedMotion() ? 0 : TAB_GROUP_MOTION_MS
}
